import {
  DecisionReason,
  InAppMessageContextMessage,
  InAppMessageExperimentContext
} from "../../../../../../model/model"
import ObjectUtil from "../../../../../../util/ObjectUtil"
import { EvaluatorContext } from "../../../../../evaluator/Evaluator"
import EvaluateRequest from "../../../../../EvaluateRequest"
import ExperimentReferenceLocalEvaluator from "../../../../experiment/mode/local/ExperimentReferenceLocalEvaluator"
import EvaluationEventRecorder from "../../../../../event/EvaluationEventRecorder"
import LocalEvaluator from "../../../../../mode/local/LocalEvaluator"
import { InAppMessageLayoutEvaluateResult } from "../../InAppMessageLayoutEvaluateResult"
import { InAppMessageLayoutEvaluateResponse } from "../../InAppMessageLayoutEvaluateResponse"
import { InAppMessageLayoutLocalEvaluateRequest } from "./InAppMessageLayoutLocalEvaluateRequest"
import { InAppMessageLayoutSelector } from "../../match/InAppMessageLayoutSelector"
import InAppMessageLayoutEvaluator from "../../InAppMessageLayoutEvaluator"

export class InAppMessageLayoutLocalEvaluator
  extends LocalEvaluator<InAppMessageLayoutLocalEvaluateRequest, InAppMessageLayoutEvaluateResponse>
  implements InAppMessageLayoutEvaluator<InAppMessageLayoutLocalEvaluateRequest>
{
  constructor(
    private readonly experimentReferenceLocalEvaluator: ExperimentReferenceLocalEvaluator,
    private readonly selector: InAppMessageLayoutSelector,
    private readonly eventRecorder: EvaluationEventRecorder
  ) {
    super()
  }

  /**
   * @override
   */
  supports(request: EvaluateRequest): request is InAppMessageLayoutLocalEvaluateRequest {
    return request instanceof InAppMessageLayoutLocalEvaluateRequest
  }

  /**
   * @override
   */
  protected doEvaluate(
    request: InAppMessageLayoutLocalEvaluateRequest,
    context: EvaluatorContext
  ): InAppMessageLayoutEvaluateResponse {
    const experimentContext = request.inAppMessage.messageContext.experimentContext
    const message =
      experimentContext !== null
        ? this.evaluateExperiment(request, context, experimentContext)
        : this.evaluateDefault(request)
    const result = InAppMessageLayoutEvaluateResult.of(DecisionReason.IN_APP_MESSAGE_TARGET, message)
    return InAppMessageLayoutEvaluateResponse.of(request, context, result)
  }

  private evaluateDefault(request: InAppMessageLayoutLocalEvaluateRequest): InAppMessageContextMessage {
    const langCondition = LangCondition(request.inAppMessage.messageContext.defaultLang)
    return this.selector.select(request.inAppMessage, langCondition)
  }

  private evaluateExperiment(
    request: InAppMessageLayoutLocalEvaluateRequest,
    context: EvaluatorContext,
    experimentContext: InAppMessageExperimentContext
  ): InAppMessageContextMessage {
    const experiment = ObjectUtil.requiredNotNullOrUndefined(
      request.workspace.getExperimentOrNull(experimentContext.key),
      () => `Experiment[key=${experimentContext.key}]`
    )
    const experimentEvaluation = this.experimentReferenceLocalEvaluator.evaluate(request, context, experiment)

    const langCondition = LangCondition(request.inAppMessage.messageContext.defaultLang)
    const experimentCondition = ExperimentCondition(experimentEvaluation.result.variation.key)
    return this.selector.select(
      request.inAppMessage,
      (message) => langCondition(message) && experimentCondition(message)
    )
  }

  /**
   * @override
   */
  record(_request: InAppMessageLayoutLocalEvaluateRequest, response: InAppMessageLayoutEvaluateResponse): void {
    this.eventRecorder.record(response)
  }
}

function LangCondition(lang: string) {
  return (message: InAppMessageContextMessage) => lang === message.lang
}

function ExperimentCondition(variationKey: string) {
  return (message: InAppMessageContextMessage) => variationKey === message.variationKey
}
